import React, { useCallback, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Paper, Portal } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { MyGroupedByPicker } from "~/components/MyWork/MyTasks/MyGroupedByPicker";
import { MyTasksColumnsProvider } from "~/components/MyWork/MyTasks/MyTasksColumnsProvider";
import { MyWorkProjectsReorderControl } from "~/components/MyWork/MyTasks/MyWorkProjectsReorderControl";
import { useUserTasksRealtime } from "~/components/MyWork/useUserTasksRealtime";
import { TeamTasksGroupedByProjectList } from "~/pages/People/Teams/TeamTasksGroupedByProjectList.jsx";
import { useMyWorkDisplaySettings } from "~/repository";
import { editPersonalizedConfigOptions } from "~/store/actions/actions.personalizedConfigOptions";
import { getRawPersonalizedConfigOption } from "~/store/selectors/selector.configOptions";
import { getUserHaveAssignments, getUserWorkProjects } from "~/store/selectors/selector.userTasks.jsx";
import { UserTaskGroupedByDateList } from "./MyTasks/UserTaskGroupedByDateList.jsx";
import { UserTasksEmptySlate } from "./UserTasksEmptySlate.jsx";

const UserTasksList = ({ userId }) => {
  const dispatch = useDispatch();
  const [hasDelighter, setHasDelighter] = useState(false);
  const configOption = useMemo(() => {
    return userId ? `user_profile_tasks_group_by` : `my_work_tasks_grouped_by`;
  }, [userId]);
  const haveAssignments = useSelector((state) => getUserHaveAssignments(state, userId));
  const groupedBy = useSelector((state) => {
    const groupBy = getRawPersonalizedConfigOption(state, configOption);
    return groupBy ?? "project";
  });
  const { isLoading, data: { task_signifiers = [], columns = [] } = {} } = useMyWorkDisplaySettings();
  const projects = useSelector(getUserWorkProjects);

  const resolvedColumns = useMemo(() => {
    if (userId) {
      return columns.map((c) => {
        return { ...c, value: true };
      });
    }

    return columns;
  }, [columns, userId]);

  const resolvedSignifiers = useMemo(() => {
    if (userId) {
      return task_signifiers.map((c) => {
        return { ...c, value: true };
      });
    }

    return task_signifiers;
  }, [task_signifiers, userId]);

  const handleChangeGroupedBy = useCallback(
    (group) => {
      dispatch(
        editPersonalizedConfigOptions({
          my_work_tasks_grouped_by: group,
        })
      );
    },
    [dispatch]
  );

  useUserTasksRealtime(userId);

  return (
    <div className={classNames("tw-flex tw-flex-col", { "tw-grow": !hasDelighter || groupedBy === "project" })}>
      <Paper
        type="paper-2"
        className="tw-mb-8 tw-flex-1 tw-relative tw-py-2"
      >
        <div
          id="tasks-list-view"
          className="tw-h-full tw-pb-8"
        >
          <MyTasksColumnsProvider
            columns={resolvedColumns}
            taskSignifiers={resolvedSignifiers}
          >
            {isLoading ? null : groupedBy !== "project" ? (
              <UserTaskGroupedByDateList
                haveAssignments={haveAssignments}
                setHasDelighter={setHasDelighter}
                userId={userId}
              />
            ) : haveAssignments ? (
              <TeamTasksGroupedByProjectList
                isForUserProfile
                membersIds={userId ? [userId] : null}
              />
            ) : (
              <UserTasksEmptySlate userId={userId} />
            )}
            {!userId ? (
              <Portal container={document.getElementById("section-controls")}>
                <>
                  {groupedBy === "project" && projects?.length > 1 ? (
                    <MyWorkProjectsReorderControl className="tw-mr-3" />
                  ) : null}
                  <MyGroupedByPicker
                    groupedBy={groupedBy}
                    onChange={handleChangeGroupedBy}
                  />
                </>
              </Portal>
            ) : null}
          </MyTasksColumnsProvider>
        </div>
      </Paper>
    </div>
  );
};

UserTasksList.propTypes = {
  userId: PropTypes.number,
};

export default UserTasksList;
